import { type Theme } from '#app/theme/index.js';
import { type Avatar } from '../Avatar/Avatar.js';
import { type AvatarSize } from '../Avatar/types.js';
import { AvatarWithPopover } from '../AvatarWithPopover/AvatarWithPopover.js';
import { type ContactAvatar } from '../ContactAvatar/ContactAvatar.js';
import { MoreIndicator } from './MoreIndicator.js';
import { type PositioningStrategy } from '@popperjs/core';
import { type ComponentPropsWithoutRef } from 'react';
import { css, styled, useTheme } from 'styled-components';

type AvatarGroupVariant = 'stack';

const DEFAULT_MAX_COUNT: { [key in AvatarGroupVariant]: number } = {
  stack: 6,
};

const getColor = (
  color: keyof Theme['colors'] | string,
  theme: Theme,
): string => {
  if (Object.keys(theme.colors).includes(color)) {
    return theme.colors[color as keyof Theme['colors']];
  }

  return color;
};

const getStackMargin = (size: AvatarSize, margin?: number): number => {
  if (margin) {
    return margin;
  }

  if (typeof size === 'number') {
    if (size <= 32) return 4;

    return 8;
  }

  switch (size) {
    case 'sm': {
      return 8;
    }

    case 'xs': {
      return 4;
    }

    default: {
      return 8;
    }
  }
};

const Group = styled.div<{
  margin?: number;
  size: AvatarSize;
  useAlternativeStackingOrder: boolean;
}>`
  ${({ margin, size, useAlternativeStackingOrder }) => css`
    display: inline-flex;
    flex-direction: ${useAlternativeStackingOrder ? 'row' : 'row-reverse'};
    justify-content: flex-end;

    & > *:not(:last-child) {
      ${useAlternativeStackingOrder
        ? css`
            margin-right: -${getStackMargin(size, margin)}px;
          `
        : css`
            margin-left: -${getStackMargin(size, margin)}px;
          `}
    }
  `}
`;

const StyledItemComponent = styled.div<{
  $outlineColor: string;
  $outlineWidth: number;
}>`
  box-shadow: 0 0 0 ${(props) => props.$outlineWidth}px
    ${(props) => props.$outlineColor};
`;

type AvatarGroupProps = {
  readonly borderColor?: keyof Theme['colors'] | string;
  readonly borderWidth?: number;
  // TODO: Infer props from component
  readonly data: ReadonlyArray<
    ComponentPropsWithoutRef<
      typeof Avatar | typeof AvatarWithPopover | typeof ContactAvatar
    > & {
      component?:
        | typeof Avatar
        | typeof AvatarWithPopover
        | typeof ContactAvatar;
      key: string;
    } & { size?: AvatarSize }
  >;
  readonly dataCount?: number;
  readonly margin?: number;
  readonly maxCount?: number;
  // TODO: Type better
  readonly onAvatarClick?: () => void;
  // TODO: Type better.
  readonly onMoreClick?: () => void;
  readonly shouldShowMoreIndicator?: boolean;
  readonly size?: AvatarSize;
  readonly strategy?: PositioningStrategy;
  readonly useAlternativeStackingOrder?: boolean;
  readonly variant?: AvatarGroupVariant;
};

export const AvatarGroup = ({
  borderColor = 'brandWhite',
  borderWidth = 2,
  data,
  dataCount,
  // onAvatarClick,
  margin,
  maxCount = 0,
  onMoreClick,
  shouldShowMoreIndicator = true,
  size = 'sm',
  strategy = 'fixed',
  useAlternativeStackingOrder = false,
  variant = 'stack',
}: AvatarGroupProps) => {
  const max: number = maxCount <= 0 ? DEFAULT_MAX_COUNT[variant] : maxCount;
  const total = dataCount ?? data.length;

  const theme = useTheme();

  const outlineColor = getColor(borderColor, theme);

  const items = data
    .slice(0, max)
    .map(
      ({ component: Component = AvatarWithPopover, key, onClick, ...item }) => {
        return (
          <StyledItemComponent
            $outlineColor={outlineColor}
            $outlineWidth={borderWidth}
            as={Component}
            key={key}
            onClick={onClick}
            {...item}
            alt=""
            size={size}
            strategy={strategy}
          />
        );
      },
    )
    .reverse();

  return (
    <Group
      margin={margin}
      size={size}
      useAlternativeStackingOrder={useAlternativeStackingOrder}
    >
      {total > max && shouldShowMoreIndicator ? (
        <MoreIndicator
          borderColor={borderColor}
          count={total - max}
          onClick={onMoreClick}
          size={size}
        />
      ) : null}
      {items}
    </Group>
  );
};
